import { AuthContext, AUTH_STATUS } from '@kargotech/tms-core/auth';
import { PageSuspenseFallback } from '@kargotech/tms-ui/components';
import { get } from 'lodash-es';
import React, { useCallback, useContext, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import MinimalLayout from '~/layouts/MinimalLayout';

interface IAuthRoute {
  children?: React.ReactNode;
  Layout?: React.FC;
}

interface ILocationState {
  from: string;
  fromSearch: string;
}

/**
 * Private Route: can only be accessed by non-authenticated user
 */
function AuthRoute({
  children,
  Layout = MinimalLayout,
}: IAuthRoute) {
  const {
    authStatus,
    enrolledCompanies,
    fetchAndSaveEnrolledCompanies,
    selectedCompanyKsuid,
    updateSessionStatus,
  } = useContext(AuthContext);

  const { pathname, state } = useLocation();

  const updateSessionAndEnrolledCompanies = useCallback(async () => {
    const isAuthenticated = await updateSessionStatus();
    if (isAuthenticated) {
      await fetchAndSaveEnrolledCompanies();
    }
  }, [fetchAndSaveEnrolledCompanies, updateSessionStatus]);

  useEffect(() => {
    updateSessionAndEnrolledCompanies();
  }, [updateSessionAndEnrolledCompanies]);

  if (authStatus === AUTH_STATUS.LOADING || (authStatus === AUTH_STATUS.AUTHENTICATED && !enrolledCompanies.length)) {
    return <PageSuspenseFallback />;
  }

  if (authStatus === AUTH_STATUS.AUTHENTICATED && selectedCompanyKsuid) {
    return (
      <Navigate
        state={{
          from: pathname,
          search: get(state as ILocationState, 'fromSearch'),
        }}
        to={get(state as ILocationState, 'from') || '/'}
      />
    );
  }

  // User who already logged in but has not choose any company will be
  // redirected to choose company page
  if (
    authStatus === AUTH_STATUS.AUTHENTICATED
    && !selectedCompanyKsuid
    && enrolledCompanies.length > 1
    && pathname !== '/auth/choose-company'
  ) {
    return <Navigate to="/auth/choose-company" />;
  }

  return (
    <Layout>
      {
        children || <Outlet />
      }
    </Layout>
  );
}

export default AuthRoute;
